import { FC } from "react";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTryUpdateLiveStep } from "@/features/procedures/hooks/useTryUpdateLiveStep";
import {
	calculateIsStepActive,
	isHeader,
	transformColorForTheme,
} from "@/features/procedures/lib/checklist";
import {
	Checklist,
	ProcedureNodeStep,
} from "@/features/procedures/model/types/checklist";
import { useChecklistView } from "@/features/procedures/ui/components/checklists/checklist/ChecklistViewContext";
import { Step } from "@/features/procedures/ui/components/checklists/checklist/step/Step";
import { Box, useTheme } from "@mui/material";

interface ChecklistRowProps {
	activeSchemeId: number | undefined;
	step: ProcedureNodeStep;
	checklist?: Checklist;
	materialHandling?: boolean;
	showCommentsAndSuggestion?: boolean | undefined;
	highlightedStepId?: number | null;
	scrollContainerId?: string;
	isProcedurePage?: boolean;
	/** When set, steps with suggestions show a Word-style comment indicator; onClick focuses the comment in the panel */
	suggestionCountByStepId?: Map<number, number>;
	onCommentIndicatorClick?: (stepId: number) => void;
}

export const ChecklistRow: FC<ChecklistRowProps> = (props) => {
	const theme = useTheme();
	const { headingFontSize } = useChecklistView();
	const isActiveStep = props.isProcedurePage
		? calculateIsStepActive(props.step, props.checklist)
		: false;
	useTryUpdateLiveStep(props.step, isActiveStep);

	/** Imported checklist header row – show document column headers */
	if (isHeader(props.step) && props.checklist?.ImportedChecklist) {
		const transformedHeaderBackground = props.step.BackgroundColor
			? transformColorForTheme(
					props.step.BackgroundColor,
					theme.palette.mode === "dark",
				)
			: null;
		return (
			<Box
				className="grid-row grid-row-header"
				style={{
					background:
						transformedHeaderBackground ||
						props.step.BackgroundColor ||
						undefined,
				}}
				sx={{ fontSize: headingFontSize, lineHeight: 1.4, px: 0 }}
			>
				<Box className="grid-column grid-column--header" sx={{ flex: "1 1 0" }}>
					<SATOSContentRender
						content={
							props.step.Value instanceof Element
								? undefined
								: (props.step.Value as string | undefined)
						}
					/>
				</Box>
				{/* Placeholder matching the fixed tools area width in imported step rows */}
				<Box sx={{ minWidth: 120, flexShrink: 0, ml: 1 }} />
			</Box>
		);
	}

	/** Manual checklist: header row hidden (we use custom table header) */
	if (isHeader(props.step)) {
		return null;
	}

	const commentIndicator =
		props.suggestionCountByStepId && props.onCommentIndicatorClick
			? (() => {
					const count = props.suggestionCountByStepId.get(props.step.Id);
					if (count == null || count < 1) return undefined;
					return {
						count,
						onClick: () => props.onCommentIndicatorClick!(props.step.Id),
					};
				})()
			: undefined;

	return (
		<Step
			activeSchemeId={props.activeSchemeId || 0}
			checklist={props.checklist}
			materialHandling={props.materialHandling}
			step={props.step}
			showCommentsAndSuggestion={props.showCommentsAndSuggestion}
			highlightedStepId={props.highlightedStepId}
			scrollContainerId={props.scrollContainerId}
			isProcedurePage={props.isProcedurePage}
			isActiveStep={isActiveStep}
			commentIndicator={commentIndicator}
		/>
	);
};
